import { COMMA } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatRadioChange } from '@angular/material/radio';
import { MatSort } from '@angular/material/sort';
import { MatStepper } from '@angular/material/stepper';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { SortAllUsers } from 'app/library/all-users/actions';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { Module } from 'app/library/module/models/module.model';
import { CreateTag, CreateTagFailure, CreateTagSuccess, SearchTags } from 'app/library/tag/actions';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagState } from 'app/library/tag/models/tag.state';
import { TagsState } from 'app/library/tag/models/tags.state';
import { TagService } from 'app/library/tag/services/tag.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { HelperService } from 'app/projects/shared/src/lib/services/helper';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { ModuleType } from '../../../enums/moduleType.enum';

@Component({
    selector: 'app-copy-to-new-module-dialog',
    templateUrl: './copy-to-new-module-dialog.component.html',
    styleUrls: ['./copy-to-new-module-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CopyToNewDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entityId: string;

    moduleType: string;
    moduleForm: FormGroup;
    currentModule: Module;
    minDate: Date;
    maxDate: Date;

    list: User[] = [];
    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    hideEmail: boolean;
    searchInput = new FormControl();

    @Select(AllUsersState.getEntities)
    entitie$: Observable<User[]>;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    @ViewChild(MatStepper) stepper: MatStepper;
    ngAfterViewInit() {
        this.stepper._getIndicatorType = () => 'number';
    }

    @Select(TagState.getUnusedColors)
    unusedColor$: Observable<string[]>;
    unusedColors: string[];

    @Select(TagsState.getFilteredEntities)
    private _filteredTag$: Observable<MaxBrainTag[]>;
    tagTermExists: boolean;
    filteredTag$: Observable<MaxBrainTag[]>;

    @ViewChild('tagInput')
    tagInput: ElementRef<HTMLInputElement>;
    @ViewChild('auto')
    matAutocomplete: MatAutocomplete;
    tagCtrl = new FormControl();

    existingModuleTags: MaxBrainTag[] = [];

    separatorKeysCodes: number[] = [COMMA];
    isCreatingTag = false;

    private _convertDurationToSeconds(value: string): number {
        const durationArray = value.split(':');

        return parseInt(durationArray[0], 10) * 3600 + parseInt(durationArray[1], 10) * 60;
    }

    private _setFormValidators(): void {
        if (this.moduleType === ModuleType.ELearning) {
            this.moduleForm.get('selfPaced').setValue(true);
        }

        if (this.moduleType === ModuleType.Basic) {
            this.moduleForm.get('startDate').setValidators(Validators.required);
            this.moduleForm.get('endDate').setValidators(Validators.required);
        }

        this.moduleForm
            .get('selfPaced')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value: boolean) => {
                if (value) {
                    this.moduleForm.get('startDate').clearValidators();
                    this.moduleForm.get('endDate').clearValidators();
                    this.moduleForm.get('startDate').setValue(null);
                    this.moduleForm.get('endDate').setValue(null);
                } else {
                    this.moduleForm.get('startDate').setValidators(Validators.required);
                    this.moduleForm.get('endDate').setValidators(Validators.required);
                }

                this.moduleForm.get('startDate').updateValueAndValidity();
                this.moduleForm.get('endDate').updateValueAndValidity();
            });
    }

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _tagService: TagService,
        public dialogRef: MatDialogRef<CopyToNewDialogComponent>,
        private _formBuilder: FormBuilder,
        private _helperService: HelperService
    ) {
        super();

        const tags = this._tagService.getTags();
        if (!tags) {
            this._tagService.fetchTags();
        }
    }

    createFormGroup(): void {
        this.moduleForm = this._formBuilder.group({
            name: ['', Validators.required],
            startDate: [''],
            endDate: [''],
            duration: [''],
            setDurationManually: [false],
            selfPaced: [false, Validators.required],
            tags: [{ value: this.existingModuleTags, disabled: false }],
            digicompDuration: this._formBuilder.group({
                y: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
                m: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
                w: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
                d: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
                hh: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
                mm: ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)],
            }),
        });
    }

    ngOnInit(): void {
        this.createFormGroup();
        this._setFormValidators();

        this.filteredTag$ = this._filteredTag$.pipe(
            filter((tags) => !!tags),
            tap((tags) => {
                const searchTerm = this._store.selectSnapshot(TagsState).searchTerm;

                if (searchTerm) {
                    this.tagTermExists = tags.map((tag) => tag.name.toLowerCase()).indexOf(searchTerm.toLowerCase()) !== -1;
                }
            })
        );

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadUsersPage(true);
        });

        this.tagCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchTags(searchTerm));
        });

        this.unusedColor$.pipe(takeUntil(this._unsubscribeAll)).subscribe((unusedColors) => {
            this.unusedColors = unusedColors;
        });

        this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);
    }

    onStepChange(event: MatStepper): void {
        if (event.selectedIndex === 2) {
            this._initializeTableData();
        }
    }

    handleChange($event: MatRadioChange): void {
        if (!$event.value) {
            this.moduleForm.controls.rule.setValue(false);
            this.moduleForm.controls.rule.disable();
        } else {
            this.moduleForm.controls.rule.enable();
        }
    }

    updateEndDate(): void {
        const startDate = this.moduleForm.get('startDate').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    updateStartDate(): void {
        const endDate = this.moduleForm.get('endDate').value;

        this.maxDate = endDate ? new Date(endDate) : null;
    }

    private _resetTagInputValue(): void {
        this.tagInput.nativeElement.value = '';
        this.tagCtrl.setValue(null);
    }

    private _addTag(tag: MaxBrainTag): void {
        if (!this.isCreatingTag) {
            this.isCreatingTag = true;

            this._store.dispatch(new CreateTag(tag));

            const actionSuccessSubscription = this._action$.pipe(ofActionDispatched(CreateTagSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: CreateTagSuccess) => {
                this.moduleForm.controls.tags.setValue([...this.moduleForm.value.tags, action.payload]);
                this.moduleForm.markAsDirty();

                this._resetTagInputValue();
                actionSuccessSubscription.unsubscribe();
                actionFailureSubscription.unsubscribe();
                this.isCreatingTag = false;
            });

            const actionFailureSubscription = this._action$.pipe(ofActionDispatched(CreateTagFailure), takeUntil(this._unsubscribeAll)).subscribe(() => {
                this._resetTagInputValue();
                actionSuccessSubscription.unsubscribe();
                actionFailureSubscription.unsubscribe();
                this.isCreatingTag = false;
            });
        }
    }

    addTag(event: MatChipInputEvent): void {
        // Add tag only when MatAutocomplete is not open
        // To make sure this does not conflict with OptionSelected Event
        if (!this.matAutocomplete.isOpen) {
            const value = event.value.trim();

            if (!value) {
                return;
            }

            const tagExists = this.moduleForm.value.tags.some((tag: MaxBrainTag) => tag.name === value);

            if (!tagExists) {
                this._addTag(
                    new MaxBrainTag({
                        id: 0,
                        name: value,
                        color: MaxBrainTag.getRandomColorFromList(this.unusedColors),
                        category: 'module',
                    })
                );
            }
        }
    }

    removeTag(tagToRemove: MaxBrainTag): void {
        this.moduleForm.controls.tags.setValue([...this.moduleForm.value.tags.filter((tag: MaxBrainTag) => tagToRemove.id !== tag.id)]);
        this.moduleForm.markAsDirty();
    }

    tagSelected(event: MatAutocompleteSelectedEvent): void {
        if (event.option.value instanceof MaxBrainTag) {
            this.moduleForm.controls.tags.setValue([...this.moduleForm.value.tags, event.option.value]);
            this.moduleForm.markAsDirty();

            this._resetTagInputValue();
        } else {
            this._addTag(
                new MaxBrainTag({
                    id: 0,
                    name: event.option.value,
                    color: MaxBrainTag.getRandomColorFromList(this.unusedColors),
                    category: 'module',
                })
            );
        }
    }

    tagUpdated(updatedTag: MaxBrainTag): void {
        this.moduleForm.patchValue({
            tags: this.moduleForm.controls.tags.value.map((tag) => {
                if (tag.id === updatedTag.id) {
                    return updatedTag;
                }
                return tag;
            }),
        });
    }

    goBack(stepper: MatStepper): void {
        stepper.previous();
    }

    goForward(stepper: MatStepper): void {
        stepper.next();
    }

    copyModule(): void {
        let formData = this.moduleForm.getRawValue();

        if (this.moduleType === ModuleType.ELearning) {
            formData.duration = this._convertDurationToSeconds(this._helperService.cleaveTimeFixer(formData.duration));
        }

        formData = Object.assign({}, formData, {
            sectionRule: 'default',
        });

        this.dialogRef.close({ data: formData, managersList: this.list });
    }

    _initializeTableData(): void {
        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            this.loadUsersPage(false);

            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }
        });
    }

    removeFromList(user: User): void {
        this.list = [...this.list.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.list = [...this.list, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    async loadUsersPage(resetPageIndex = false, isUnassignedSelected = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                'users',
                [],
                this.entityId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                isUnassignedSelected
            );
        }
    }
}
